import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/core/components/ui/button";
import { Card } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { BUSINESS_SETTINGS_ROUTES } from "@/business-settings/constants";
import { useBusiness } from "@/core/business";
import useDelayedAction from "@/core/hooks/use-delayed-action";
import { dayjs } from "@/core/utils/date";
import {
  TextMessageListItem,
  TextMessageListItemSkeleton,
} from "@/text-messages/components/text-message-list-item";
import {
  useGetInboundTextMessages,
  usePrefetchCustomerTextMessages,
} from "@/text-messages/query";
import { IMostRecentTextMessage } from "@/text-messages/text-messages";

const LOADING_TEXT_MESSAGES = Array.from({ length: 5 });

const TextMessagesPane = ({
  selectedItem,
  setSelectedItem,
  setIsSidebarOpen,
}: {
  selectedItem: IMostRecentTextMessage | null;
  setSelectedItem: (item: IMostRecentTextMessage | null) => void;
  setIsSidebarOpen: (open: boolean) => void;
}) => {
  const navigate = useNavigate();
  const { businessId } = useBusiness();
  const { data: messages, isLoading } = useGetInboundTextMessages(businessId);

  const mostRecentTextMessages = useMemo(
    () =>
      [...(messages || [])]
        .sort((a, b) => dayjs(b.created_at).diff(dayjs(a.created_at)))
        .slice(0, 5),
    [messages],
  );

  const prefetchCustomerTextMessages = usePrefetchCustomerTextMessages();

  const { schedule: schedulePrefetch, cancel: cancelPrefetch } =
    useDelayedAction(prefetchCustomerTextMessages, 250);

  return (
    <Card className="w-full rounded-2xl">
      <Stack
        direction="row"
        align="center"
        justify="between"
        className="p-6 pb-2"
      >
        <Typography
          variant="titleW2"
          className="text-[20px] font-semibold leading-7"
        >
          Text messages
        </Typography>
        <Button
          variant="link"
          size="sm"
          rightIcon={<ArrowRightIcon className="h-4 w-4" />}
          onClick={() => {
            navigate(
              `/business-settings/${businessId}${BUSINESS_SETTINGS_ROUTES.textMessages}`,
            );
          }}
        >
          View All
        </Button>
      </Stack>
      <Stack fullWidth gap="none" className="px-6 pb-6">
        {isLoading ? (
          <Stack gap="none">
            {LOADING_TEXT_MESSAGES.map((_, idx) => (
              <TextMessageListItemSkeleton key={idx} index={idx} />
            ))}
          </Stack>
        ) : (
          mostRecentTextMessages.map((t) => (
            <TextMessageListItem
              key={t.id}
              textMessage={t}
              isSelected={selectedItem?.id === t.id}
              onSelect={() => {
                setSelectedItem(t);
                setIsSidebarOpen(true);
              }}
              onMouseEnter={() => schedulePrefetch(t.customer.id)}
              onMouseLeave={cancelPrefetch}
            />
          ))
        )}
      </Stack>
    </Card>
  );
};

export default TextMessagesPane;
